import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { MediaUploadStatusOverlay } from '@euler/app/components/media/UploadStatusOverlay';
import { Typography } from '@euler/app/components/typography';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { Icon } from '@euler/components/Icomoon';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { safeMarkDevFlag, withConfirmation } from '@euler/utils';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  FlatListProps,
  ListRenderItem,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';
import { FacadeMediaInfo } from '../functions/types';

const kForceLoadingStatus = safeMarkDevFlag(false);
const kDefaultCellMargin = 5;

type Item =
  | {
      type: 'media';
      media: FacadeMediaInfo;
    }
  | {
      type: 'add';
    };

const keyExtractor = (item: Item) =>
  item.type === 'media' ? item.media.key : '__add__';

export const FacadeMediaList = memo(
  ({
    editable = true,
    medias,
    width,
    removeIconSize,
    progressViewSize,
    hideLabel,
    onDelete,
    onRetryUpload,
    onPreviewVideo,
    showsHorizontalScrollIndicator,
    addButtonStyle,
    onAdd,
    ...props
  }: {
    editable?: boolean;
    medias: FacadeMediaInfo[];
    width: number;
    cellSize?: number;
    cellMargin?: number;
    removeIconSize?: number;
    progressViewSize?: number;
    hideLabel?: boolean;
    addButtonStyle?: StyleProp<ViewStyle>;
    onDelete?: (media: FacadeMediaInfo) => void;
    onPreviewVideo?: (media: FacadeMediaInfo) => void;
    onRetryUpload?: (media: FacadeMediaInfo) => void;
    onAdd?: () => void;
  } & Omit<FlatListProps<Item>, 'data' | 'renderItem' | 'keyExtractor'>) => {
    const { rpx } = useResponsiveContext();
    const [currentIndex, setCurrentIndex] = useState<number>();

    const cellSize =
      props.cellSize ??
      (props.numColumns != null && props.numColumns > 1
        ? computeGridLayoutItemSize({
            numberOfColumns: props.numColumns,
            aspectRatio: 1,
            cellMargin: props.cellMargin ?? rpx(kDefaultCellMargin),
            gridPadding: 0,
            gridWidth: width - rpx(10) * 2,
          })[0]
        : rpx(80));

    const cellMargin =
      props.cellMargin ??
      (props.numColumns != null && props.numColumns > 1
        ? rpx(kDefaultCellMargin)
        : cellSize * (6 / 80));

    const images = useMemo(
      () => medias.filter(x => x.type === 'image') ?? [],
      [medias],
    );

    const imageUrls = useMemo(() => images.map(x => x.url), [images]);

    const items = useMemo<Item[]>(() => {
      const list: Item[] = medias.map(media => ({ type: 'media', media }));
      if (onAdd && editable) {
        list.push({ type: 'add' });
      }
      return list;
    }, [medias, onAdd, editable]);

    const currentImageIndex = useMemo(
      () =>
        currentIndex == null
          ? undefined
          : medias[currentIndex]?.type === 'image'
          ? images.findIndex(x => x.key === medias[currentIndex].key)
          : 0,
      [currentIndex, images, medias],
    );

    const onCellPress = useCallback(
      (media: FacadeMediaInfo, index: number) => {
        if (media.type === 'image') {
          setCurrentIndex(index);
        } else {
          onPreviewVideo?.(media);
        }
      },
      [onPreviewVideo],
    );

    const horizontal = props.horizontal;
    const numColumns = props.numColumns ?? 0;
    const isGridView = horizontal === true || numColumns > 0;

    const cellStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isGridView
        ? {
            width: cellSize,
            height: cellSize,
            margin: cellMargin,
          }
        : {
            height: width * 0.8,
            marginBottom: rpx(20),
          };
    }, [isGridView, cellSize, cellMargin, width, rpx]);

    const mediaContainerStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isGridView
        ? {
            borderRadius: cellSize * (6 / 80),
          }
        : {
            borderRadius: rpx(20),
          };
    }, [isGridView, cellSize, rpx]);

    const labelStyle = useMemo<StyleProp<TextStyle>>(() => {
      return isGridView
        ? {}
        : {
            fontSize: rpx(18),
            lineHeight: rpx(24),
          };
    }, [isGridView, rpx]);

    const labelContainerStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isGridView
        ? {
            borderBottomLeftRadius: cellSize * (6 / 80),
            borderBottomRightRadius: cellSize * (6 / 80),
          }
        : {
            paddingTop: rpx(8),
            paddingBottom: rpx(8),
            borderBottomLeftRadius: rpx(20),
            borderBottomRightRadius: rpx(20),
          };
    }, [isGridView, cellSize, rpx]);

    const renderItem: ListRenderItem<Item> = useCallback(
      ({ item, index }) => {
        if (item.type === 'media') {
          return (
            <MediaCell
              media={item.media}
              editable={editable}
              index={index}
              style={cellStyle}
              mediaStyle={mediaContainerStyle}
              hideLabel={hideLabel}
              labelStyle={labelStyle}
              labelContainerStyle={labelContainerStyle}
              progressViewSize={
                progressViewSize ?? (isGridView ? cellSize / 2 : width * 0.4)
              }
              playIconSize={isGridView ? cellSize * 0.22 : rpx(32)}
              removeIconSize={
                removeIconSize ?? isGridView
                  ? Math.min(rpx(21), cellSize * 0.2)
                  : rpx(28)
              }
              onPress={onCellPress}
              onDelete={onDelete}
              onRetry={onRetryUpload}
            />
          );
        }
        return (
          <TouchableOpacity
            css={`
              border-width: 1px;
              border-color: #ddd;
              border-radius: ${rpx(8)}px;
              justify-content: center;
              align-items: center;
            `}
            style={[cellStyle, addButtonStyle]}
            onPress={onAdd}
          >
            <Icon name="plus" size={cellSize / 2} color="#ddd" />
          </TouchableOpacity>
        );
      },
      [
        rpx,
        cellStyle,
        addButtonStyle,
        onAdd,
        cellSize,
        editable,
        mediaContainerStyle,
        hideLabel,
        labelStyle,
        labelContainerStyle,
        progressViewSize,
        isGridView,
        width,
        removeIconSize,
        onCellPress,
        onDelete,
        onRetryUpload,
      ],
    );

    return (
      <>
        <ImageViewer
          images={imageUrls}
          index={currentImageIndex}
          show={currentImageIndex != null}
          onDismiss={() => setCurrentIndex(undefined)}
        />
        <FlatList
          data={items}
          keyExtractor={keyExtractor}
          key={numColumns}
          showsHorizontalScrollIndicator={
            showsHorizontalScrollIndicator ?? Platform.OS === 'web'
          }
          renderItem={renderItem}
          {...props}
        />
      </>
    );
  },
);

const MediaView = memo(
  ({
    item,
    style,
    playIconSize,
    onPress,
  }: {
    item: MediaInfo;
    style?: StyleProp<ViewStyle>;
    playIconSize?: number;
    onPress?: (media: MediaObject) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const media = useMemo<MediaObject>(
      () => ({
        id: item.key,
        url: item.url,
        type: item.type,
        coverUrl: item.coverUrl,
      }),
      [item],
    );
    return (
      <MediaCoverView
        media={media}
        noPlaceholder
        onPress={onPress}
        playIconSize={playIconSize ?? rpx(24)}
        css={`
          overflow: hidden;
        `}
        style={style}
      />
    );
  },
);

const MediaCell = memo(
  ({
    media,
    playIconSize,
    editable,
    index,
    progressViewSize,
    hideLabel,
    style,
    mediaStyle,
    mediaContainerStyle,
    labelStyle,
    labelContainerStyle,
    onDelete,
    onRetry,
    onPress,
    ...props
  }: {
    media: FacadeMediaInfo;
    playIconSize?: number;
    editable?: boolean;
    hideLabel?: boolean;
    index: number;
    progressViewSize: number;
    removeIconSize?: number;
    style?: StyleProp<ViewStyle>;
    mediaStyle?: StyleProp<ViewStyle>;
    mediaContainerStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    labelContainerStyle?: StyleProp<ViewStyle>;
    onDelete?: (media: FacadeMediaInfo) => void;
    onRetry?: (media: FacadeMediaInfo) => void;
    onPress?: (media: FacadeMediaInfo, index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const removeIconSize = props.removeIconSize ?? rpx(20);
    const {
      colors: {
        status: { danger },
      },
    } = useTheme();

    const onMediaPress = useCallback(() => {
      onPress?.(media, index);
    }, [media, index, onPress]);

    const onDeletePress = useCallback(() => {
      onDelete?.(media);
    }, [media, onDelete]);

    const onRetryPress = useCallback(async () => {
      if (media.status !== 'error') return;
      const result = await withConfirmation({
        title: '是否重新上传?',
        message: '',
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (result) {
        onRetry?.(media);
      }
    }, [media, onRetry]);

    const { status } = media;

    return (
      <View style={style}>
        <View
          css={`
            flex: 1;
            overflow: hidden;
          `}
          style={mediaContainerStyle}
        >
          <MediaView
            item={media}
            playIconSize={playIconSize}
            onPress={onMediaPress}
            style={mediaStyle}
          />
          {!hideLabel && (
            <View
              css={`
                background-color: rgba(0, 0, 0, 0.5);
                padding: ${rpx(3)}px;
                position: absolute;
                left: 0px;
                right: 0;
                bottom: 0px;
                align-items: center;
              `}
              style={labelContainerStyle}
            >
              <Typography.Label
                size={rpx(12)}
                color="#fff"
                light
                numberOfLines={1}
                style={labelStyle}
              >
                {media.siteName}
              </Typography.Label>
            </View>
          )}
        </View>
        {editable && (
          <>
            {(status != null || kForceLoadingStatus) && (
              <MediaUploadStatusOverlay
                status={
                  kForceLoadingStatus ? 'uploading' : status ?? 'uploading'
                }
                showSuccessCheckmark={false}
                progressViewSize={progressViewSize}
                progress={media.progress ?? 0.75}
                onRetry={onRetryPress}
                style={StyleSheet.absoluteFill}
                css={`
                  border-radius: ${rpx(6)}px;
                `}
              />
            )}
            <TouchableOpacity
              onPress={onDeletePress}
              css={`
                position: absolute;
                right: ${-removeIconSize * 0.35}px;
                top: ${-removeIconSize * 0.35}px;
                width: ${removeIconSize}px;
                height: ${removeIconSize}px;
                border-radius: ${removeIconSize / 2}px;
                background-color: ${danger};
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name="close" size={removeIconSize * 0.7} color="#fff" />
            </TouchableOpacity>
          </>
        )}
      </View>
    );
  },
);
